import React, { useState } from "react";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAdminAuth } from "@/contexts/admin-auth";
import { useToast } from "@/hooks/use-toast";

interface DeleteAssessmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assessmentId: string;
  participantName?: string | null;
  participantEmail?: string | null;
  onDeleted: () => void;
}

export function DeleteAssessmentDialog({
  open,
  onOpenChange,
  assessmentId,
  participantName,
  participantEmail,
  onDeleted,
}: DeleteAssessmentDialogProps) {
  const { authHeader } = useAdminAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  const participantLabel = participantName ?? participantEmail ?? assessmentId;

  const handleDelete = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${import.meta.env.BASE_URL}api/admin/assessments/${encodeURIComponent(assessmentId)}`,
        { method: "DELETE", headers: { Authorization: authHeader ?? "" } },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { error?: string }).error ?? `Erro ${res.status}`);
      }
      onOpenChange(false);
      onDeleted();
      toast({ title: "Avaliação excluída", description: `A avaliação de ${participantLabel} foi removida permanentemente.` });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Erro desconhecido";
      toast({ title: "Erro ao excluir", description: msg, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!loading) onOpenChange(v); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Excluir avaliação?</DialogTitle>
          <DialogDescription className="space-y-2 pt-1">
            <span className="block">
              Você está prestes a excluir permanentemente a avaliação de{" "}
              <strong>{participantLabel}</strong>.
            </span>
            <span className="block text-destructive font-medium">
              Esta ação é irreversível. Todos os dados associados (mensagens, respostas e resultados DISC) serão removidos.
            </span>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={loading}
            data-testid="button-confirm-delete"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            {loading ? "Excluindo…" : "Excluir avaliação"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
